import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserClient, UserTransferModel } from '@lib-ui/client';
import { merge, of, Subject, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { LoggingService } from '@lib-ui/lib/services/logging.service';
import { LoadingState, ServiceState } from '@lib-ui/lib/models';

interface UserListState extends ServiceState {
  users: Signal<UserTransferModel[]>;
}

@Injectable({
  providedIn: 'root',
})
export class UserListService {
  private readonly userClient = inject(UserClient);
  private readonly loggingService = inject(LoggingService);
  private readonly load$ = new Subject<void>();
  private readonly error$ = new Subject<Error | null>();
  private readonly users$ = this.load$.pipe(
    startWith(null),
    switchMap(() => {
      return this.userClient.getAllUsers().pipe(
        catchError((error) => {
          this.error$.next(error);
          return of([]);
        }),
      );
    }),
    shareReplay(1),
  );

  private readonly status$ = merge(
    this.users$.pipe(map(() => LoadingState.Success)),
    this.error$.pipe(map(() => LoadingState.Error)),
    this.load$.pipe(map(() => LoadingState.Pending)),
  );

  private readonly users = toSignal(this.users$, { initialValue: [] });
  private readonly status = toSignal(this.status$, { initialValue: LoadingState.Pending });
  private readonly error = toSignal(this.error$, { initialValue: null });

  public state: UserListState = {
    users: this.users,
    status: this.status,
    error: this.error,
  };

  load() {
    this.load$.next();
  }

  deleteUsers(users: UserTransferModel[]) {
    const ids = users
      .map(u => u.id)
      .filter((id): id is number => !!id);
    return this.userClient.deleteUsers(ids).pipe(
      catchError(error => {
        this.loggingService.logException(error);
        return throwError(error);
      }),
      finalize(() => this.load()),
    );
  }

  deleteUser(user: UserTransferModel) {
    const userId = user.id;
    if (!userId) {
      return of();
    }
    return this.userClient.deleteUser(userId).pipe(
      catchError(error => {
        this.loggingService.logException(error);
        return throwError(error);
      }),
      finalize(() => this.load()),
    );
  }

  migrateUsers(users: UserTransferModel[]) {
    const ids = users
      .map(u => u.id)
      .filter((id): id is number => !!id);
    return this.userClient.migrateUsers(ids).pipe(
      catchError(error => {
        this.loggingService.logException(error);
        return throwError(error);
      }),
      finalize(() => this.load()),
    );
  }

  getUserById(userId: number) {
    return this.userClient.getUserById(userId).pipe(
      catchError(error => {
        this.loggingService.logException(error);
        return of(null);
      }),
    );
  }
}
